import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { PaymentType, PricingPlanFeature, PricingPlanSchema, UserActionType } from 'types';
import { currentPlanSelector, isAuthSelector, profileChannelNameSelector } from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { api, LoggerService } from 'services';
import { useRouter, useStore } from 'hooks';
import { PricingPlanCard, Text } from 'components';
import { Spinner } from 'components/Icon';

import styles from './PricingPlans.module.scss';

export type PricingPlansProps = {
  membersOnly?: boolean;
  recording?: boolean;
};

const PricingPlans = ({ membersOnly = false, recording = false }: PricingPlansProps): JSX.Element => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { currentPlan, isAuth, profileChannelName } = useStore(state => ({
    currentPlan: currentPlanSelector(state),
    isAuth: isAuthSelector(state),
    profileChannelName: profileChannelNameSelector(state)
  }));

  const [isFetching, setFetching] = useState(false);
  const [plans, setPlans] = useState<PricingPlanSchema[]>([]);

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const fetchPricingPlans = async () => {
    setFetching(true);

    try {
      const { data } = await api.requests.ltv.getPricingPlans();
      let planList = data;

      if (membersOnly) {
        // TODO: need to apply filter to API
        planList = planList.filter(plan => plan.features.includes(PricingPlanFeature.MEMBERS_ONLY_EVENT));
      } else if (recording) {
        // TODO: need to apply filter to API
        planList = planList.filter(plan => plan.features.includes(PricingPlanFeature.RECORD_VIDEO));
      }

      setPlans(planList);
    } catch (error) {
      LoggerService.log('FETCH_PRICING_PLANS', error);
    } finally {
      setFetching(false);
    }
  };

  const handleOnActivateFreeTrial = () => {
    if (!isAuth) {
      dispatch(callbackTriggersActions.set([{ type: UserActionType.REDIRECT, payload: { path: router.pathname } }]));

      router.push('/login');
      return;
    }

    if (!profileChannelName) {
      dispatch(callbackTriggersActions.add([{ type: UserActionType.OPEN_FREE_TRIAL_PANEL }]));

      router.replace('/');
    }
  };

  const handleOnClickPurchase = (pricingPlanId: string) => {
    if (!isAuth) {
      dispatch(
        callbackTriggersActions.set([
          {
            type: UserActionType.REDIRECT,
            payload: { path: '/payment', params: { type: PaymentType.CREATOR, pricingPlanId } }
          }
        ])
      );

      router.push('/login');
      return;
    }

    if (!profileChannelName) {
      dispatch(callbackTriggersActions.add([{ type: UserActionType.OPEN_FREE_TRIAL_PANEL }]));

      router.replace('/');
      return;
    }
    router.push(`/payment`, { type: PaymentType.CREATOR, pricingPlanId });
  };

  if (isFetching) {
    return (
      <div className="text-center py-42">
        <Spinner />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <Text size="h4" className="text-center py-48">
        No plans found
      </Text>
    );
  }

  return (
    <div className={styles.container}>
      <ul className={styles.list}>
        {plans.map(plan => (
          <li key={plan.id} className={styles.item}>
            <PricingPlanCard
              {...plan}
              isPurchasable
              isCurrentPlan={currentPlan?.id === plan.id}
              onPurchase={handleOnClickPurchase}
              onActivateFreeTrial={handleOnActivateFreeTrial}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PricingPlans;
